import { Component, type ErrorInfo, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { queryClient } from './lib/queryClient';
import { AppShell } from './app/AppShell';
import { ProtectedRoute } from './app/ProtectedRoute';
import { HomePage } from './pages/HomePage';
import { BookingPage } from './pages/BookingPage';
import { FaqPage } from './pages/FaqPage';
import { ProfilePage } from './pages/ProfilePage';
import { ContactsPage } from './pages/ContactsPage';
import { DirectorPage } from './pages/DirectorPage';
import { AlbumPage } from './pages/AlbumPage';
import { PagePage } from './pages/PagePage';
import { MenuPage } from './pages/MenuPage';
import { LegalPage } from './pages/LegalPage';
import { AnalyticsTracker } from './app/AnalyticsTracker';

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info);
  }
  override render() {
    if (this.state.error) {
      return (
        <div className="p-6 text-center">
          <div className="font-semibold mb-2">Что-то пошло не так</div>
          <div className="text-hint text-sm">{this.state.error.message}</div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <BrowserRouter basename={import.meta.env.BASE_URL}>
            <AnalyticsTracker />
            <Routes>
              <Route
                element={
                  <ProtectedRoute>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<HomePage />} />
                <Route path="/booking" element={<BookingPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/director" element={<DirectorPage />} />
                <Route path="/albums" element={<AlbumPage />} />
                <Route path="/albums/:id" element={<AlbumPage />} />
                <Route path="/page/:slug" element={<PagePage />} />
                <Route path="/menu" element={<MenuPage />} />
                <Route path="/legal/:slug" element={<LegalPage />} />
                <Route path="*" element={<HomePage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </ErrorBoundary>
      </QueryClientProvider>
    </I18nextProvider>
  );
}
